import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Chip,
    IconButton,
    Grid,
    Paper,
    Pagination,
    PaginationItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import { 
    getAllDistrictsAction,
} from "../../../store/actions";
import AddDistrict from "./AddDistrict";

const District = () => {
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedDistrictId, setSelectedDistrictId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 30;
    const dispatch = useDispatch();

    const { successData: districtsData, loading: districtsLoading } = useSelector((state) => state.getAllDistrictsReducer || {});

    // Extract raw districts array from Redux response
    const rawDistricts = useMemo(() => {
        if (Array.isArray(districtsData)) return districtsData;
        if (Array.isArray(districtsData?.data?.districts)) return districtsData.data.districts;
        return [];
    }, [districtsData]);

    // Normalize once immediately after reading from Redux
    const normalizedDistricts = useMemo(() => {
        return rawDistricts.map((d) => ({
            districtId: String(d.districtId),
            name: d.name,
            areaId: Number(d.areaId),
            areaName: d.areaName,
            regionId: Number(d.regionId),
            regionName: d.regionName,
            zoneId: Number(d.zoneId),
            zoneName: d.zoneName,
            status: d.status !== undefined && d.status !== null && d.status !== "" ? d.status : "Active",
        }));
    }, [rawDistricts]);

    // Search/filter only on normalized fields
    const filteredDistricts = useMemo(() => {
        const q = (searchQuery || "").trim().toLowerCase();
        if (!q) return normalizedDistricts;
        return normalizedDistricts.filter(({
            districtId,
            name,
            areaName,
            regionName,
            zoneName,
            status,
        }) => {
            return (
                String(districtId).toLowerCase().includes(q) ||
                String(name).toLowerCase().includes(q) ||
                String(areaName).toLowerCase().includes(q) ||
                String(regionName).toLowerCase().includes(q) ||
                String(zoneName).toLowerCase().includes(q) ||
                String(status).toLowerCase().includes(q)
            );
        });
    }, [normalizedDistricts, searchQuery]);

    // Pagination derived values
    const totalPages = useMemo(() => {
        const pages = Math.ceil(filteredDistricts.length / rowsPerPage);
        return Math.max(1, pages || 1);
    }, [filteredDistricts.length]);

    const paginatedDistricts = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredDistricts.slice(start, start + rowsPerPage);
    }, [filteredDistricts, page]);

    const showingStart = filteredDistricts.length === 0 ? 0 : ((page - 1) * rowsPerPage + 1);
    const showingEnd = Math.min(page * rowsPerPage, filteredDistricts.length);

    useEffect(() => {
        dispatch(getAllDistrictsAction());
    }, [dispatch]);

    useEffect(() => {
        // Reset page when search filter changes
        setPage(1);
    }, [searchQuery]);

    useEffect(() => {
        // Clamp page if total pages shrink
        if (page > totalPages) {
            setPage(1);
        }
    }, [totalPages, page]);

    const handleAdd = () => {
        setIsEdit(false);
        setSelectedDistrictId("");
        setOpen(true);
    };

    const handleEdit = (districtId) => {
        if (!districtId) return;
        setIsEdit(true);
        setSelectedDistrictId(String(districtId));
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedDistrictId("");
    };

    const getStatusChipStyles = (status) => {
        const s = (status || '').toString().toLowerCase();
        if (s === 'active') return { backgroundColor: '#D1FAE5', color: '#047857' };
        if (s === 'inactive') return { backgroundColor: '#FECACA', color: '#B91C1C' };
        if (s === 'pending') return { backgroundColor: '#FDE68A', color: '#92400E' };
        return { backgroundColor: '#E5E7EB', color: '#111827' };
    };

    return (
        <Box sx={{ minHeight: '100vh', mt: 2 }}>
            {/* Header */}
            <Typography fontSize={18} fontWeight={600} color="#111827" mb={2}>
                District Management
            </Typography>

            {/* Search + Button */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 3,
                    width: { xs: 330, sm: 'auto' },
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                <TextField
                    placeholder="Search ..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        width: { xs: 300, sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                        },
                        '& .MuiInputBase-input': {
                            borderRadius: 3,
                        },
                    }}
                />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: "#4F46E5",
                        textTransform: "none",
                        borderRadius: 3,
                        width:{xs:300},
                        px: 3,
                    }}
                    onClick={handleAdd}
                >
                    Add New District
                </Button>
            </Box>

            {/* Table for sm+ screens - Zone-style grid */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {/* Header */}
                <Box
                    display={{ xs: 'none', sm: 'grid' }}
                    gridTemplateColumns={{ sm: '1fr 1.6fr 1fr 1fr 1fr 1fr 1fr' }}
                    alignItems="center"
                    px={{ xs: 1, sm: 2 }}
                    py={{ xs: 1, sm: 2 }}
                    mb={{ xs: 1, sm: 1 }}
                >
                    {['DISTRICT ID', 'DISTRICT NAME', 'AREA', 'REGION', 'ZONE', 'STATUS', 'ACTIONS'].map((label, i) => (
                        <Typography
                            key={label}
                            fontSize={{ sm: 10, md: 12 }}
                            color="#000000"
                            fontWeight={700}
                            sx={{
                                textTransform: 'uppercase',
                                textAlign: i === 5 ? 'center' : i === 6 ? 'right' : 'left',
                            }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                {districtsLoading && (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', px: 2, py: 1.5, mb: 2 }}>
                        <Typography variant="body2">Loading districts...</Typography>
                    </Paper>
                )}
                {!districtsLoading && paginatedDistricts.map(({ districtId, name, areaName, regionName, zoneName, status }) => {
                    const chipSx = {
                        width: 90,
                        height: 24,
                        ...getStatusChipStyles(status),
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                    };
                    return (
                        <Paper
                            key={districtId || name}
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: '1fr 1.6fr 1fr 1fr 1fr 1fr 1fr' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                px: 2,
                                py: 1.5,
                                gap: 0,
                                columnGap: 0,
                                mb: 1.25,
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{districtId}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{name}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{areaName}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{regionName}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{zoneName}</Typography>
                            <Box sx={{ justifySelf: 'center' }}>
                                <Chip label={String(status).charAt(0).toUpperCase() + String(status).slice(1)} size="small" sx={chipSx} />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton size="small" aria-label="edit district" onClick={() => handleEdit(districtId)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Cards for xs screens */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }} justifyContent="center">
                {!districtsLoading && paginatedDistricts.map(({ districtId, name, areaName, regionName, zoneName, status }) => {
                    return (
                    <Grid item xs={12} key={districtId || name} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                width: 300,
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                                <Box />
                                <Chip
                                    label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                                    size="small"
                                    sx={{ ...getStatusChipStyles(status), fontWeight: 600 }}
                                />
                            </Box>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" fontWeight={600} color="#0F172A">District: {name}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">District ID: {districtId}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Area: {areaName}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Region: {regionName}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Zone: {zoneName}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <IconButton size="small" aria-label="edit district" onClick={() => handleEdit(districtId)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    </Grid>
                    );
                })}
            </Grid>

            <AddDistrict
                open={open}
                onClose={handleCloseDialog}
                isEdit={isEdit}
                districtId={selectedDistrictId}
                onSuccess={() => dispatch(getAllDistrictsAction())}
            />

            {/* Footer Pagination */}
            {!districtsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Showing {showingStart}-{showingEnd} of {filteredDistricts.length}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        size="small"
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={{
                                    borderRadius: '50%',
                                    minWidth: 32,
                                    height: 32,
                                    '&.Mui-selected': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            )}
        </Box>
    );
};

export default District;
